import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'

import { useUserContext } from '../../auth/UserProvider'
import useTranslations from '../../i18n/useTranslations'
import useEnhancedFormik from '../../hooks/useEnhancedFormik'
import ConfirmationWithErrorModal from '../../ui/modals/confirmationWithError'
import ActionButtons from '../../ui/views/shippingSend/ActionButtons'
import ShippingSendForm from '../../ui/views/shippingSend/FormWrapper'
import Step2DestinationUI from '../../ui/views/shippingSend/Step2Destination'
import { STEPS, ADDRESS_STATES } from '../../common/constants'
import {
  shippingSend2ToPropTypes,
  nullablePropType,
} from '../../common/propTypes'
import {
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../common/formikPropTypes'
import {
  generateTouchedObject,
  setFormikStateAndValidate,
} from '../../libs/formik'

import useDebounce from './useDebounce'
import calculateProvinceAndLocalities, {
  GEOCODING_ERRORS,
} from './calculateProvinceAndLocalities'
import { getCompleteAddressFromData } from '../business'
import { formatCentreAddress } from './formatters'
import useShippingSendStep from './useShippingSendStep'
import AddressBook from './AddressBook'
import { DEBOUNCED_POSTAL_CODE_DELAY } from './Step1Origin'
import { useDestinationCountries } from '../../services/destinationCountries'
import useDeliveryPoints from '../../services/deliveryPoints'
import { isValidPhoneNumber } from 'react-phone-number-input'

const getDestinationSchema = () => {
  return Yup.object().shape({
    addressState: Yup.string().oneOf(Object.keys(ADDRESS_STATES)).required(),
    address: Yup.object().when('addressState', {
      is: ADDRESS_STATES.NEW,
      then: Yup.object().shape({
        country: Yup.object().shape({
          label: Yup.string().required(),
          code: Yup.string().required(),
        }),
        address: Yup.string().required(),
        name: Yup.string().required(),
        phonePrefix: Yup.string(),
        phone: Yup.string()
          .required()
          .test(
            'isValidPhoneNumber',
            {
              key: 'shipping-send-form:new-address:phone-format-or-length-error',
            },
            function (value, schema) {
              const phonePrefix = schema.parent.phonePrefix
              const fullPhoneNumber = `${phonePrefix}${value}`
              return isValidPhoneNumber(fullPhoneNumber)
            },
          ),
        email: Yup.string().email(),
        stair: Yup.string(),
        floor: Yup.string(),
        letter: Yup.string(),
        postalCode: Yup.string().when('country.code', {
          is: 'ES',
          then: Yup.string()
            .length(5, {
              key: 'shipping-send-form:origin:postal-code:invalid-format',
            })
            .required({
              key: 'error:field:postal-code:required',
            }),
          otherwise: Yup.string().when('country.code', {
            is: 'PT',
            then: Yup.string()
              .matches(/^(?:\d{4}|\d{4}-\d{3})$/, {
                key: 'shipping-send-form:origin:postal-code:invalid-format',
              })
              .required({
                key: 'error:field:postal-code:required',
              }),
            otherwise: Yup.string().required({
              key: 'error:field:postal-code:required',
            }),
          }),
        }),
        province: Yup.string().required(),
        city: Yup.string().required(),
        schedule: Yup.array().nullable(),
        contactPerson: Yup.string(),
      }),
    }),
    comments: Yup.string(),
  })
}

const DEFAULT_PHONE_PREFIX = '+34'

const INITIAL_VALUES = {
  addressState: ADDRESS_STATES.NEW,
  addressBook: null,
  selectedDeliveryPoint: undefined,
  deliveryPointAddress: undefined,
  confirmedAddress: undefined,
  address: {
    country: { code: 'ES', label: 'España' },
    name: '',
    address: '',
    phone: '',
    phonePrefix: DEFAULT_PHONE_PREFIX,
    email: '',
    stair: '',
    floor: '',
    letter: '',
    postalCode: '',
    province: '',
    city: '',
    contactPerson: '',
    hasSeveralLocalities: false,
    localitiesOptions: null,
    schedule: null,
  },
  comments: '',
}

const Step2Destination = ({
  centreCode,
  savedValues,
  originCentreId,
  activeStep,
  serverFieldErrors,
  hasOtherError,
  onSubmitSuccess,
  onCancel,
  onSetDirty,
  hasConfirmation,
  onCancelConfirmation,
  onAcceptConfirmation,
}) => {
  const t = useTranslations()
  const intl = useIntl()
  const { centres } = useUserContext()
  const { data: countries } = useDestinationCountries()

  // NOTE: mirar el comentario de este mismo estado en el archivo
  // `src/context/ShippingSend/Step1Origin/index.jsx`.
  const [zeroResultsGeocode, setZeroResultsGeocode] = useState(false)

  const [initialLoad, setInitialLoad] = useState(true)

  const [error, setError] = useState(false)
  const [fromSearch, setFromSearch] = useState(false)

  const handlePickUpErrors = hasErrors => {
    setError(hasErrors)
  }

  const [isAddressBookOpen, setIsAddressBookOpen] = useState(false)
  const closeAddressBook = () => {
    setIsAddressBookOpen(false)
  }

  const formik = useFormik({
    validateOnChange: false,
    validationSchema: getDestinationSchema(),
    initialValues: savedValues ?? INITIAL_VALUES,
    initialTouched: !!savedValues
      ? generateTouchedObject(shippingSend2ToPropTypes, savedValues)
      : undefined,
    onSubmit: values => {
      return onSubmitSuccess({ ...values, addressBook })
    },
  })

  const deliveryPoints = useDeliveryPoints(
    {
      lat: formik.values.deliveryPointAddress?.location.lat,
      lng: formik.values.deliveryPointAddress?.location.lng,
      address: formik.values.deliveryPointAddress?.address,
      city: 'Madrid',
      postalCode:
        formik.values.deliveryPointAddress?.postalCode?.postalCode ??
        formik.values.deliveryPointAddress?.postalCode,
      countryCode: formik.values.deliveryPointAddress?.country.code,
      dimensions: [
        {
          width: 0,
          height: 0,
          depth: 0,
          magnitude: 'CM',
        },
      ],
      range: 3,
    },
    t,
  )

  const confirmAddress = address => {
    formik.setValues(values => ({
      ...values,
      confirmedAddress: address,
    }))
  }

  const changeDeliveryPointAddress = address => {
    formik.setValues(values => ({
      ...values,
      deliveryPointAddress: address,
    }))
  }

  // const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(
  //   savedValues && savedValues.addressState === ADDRESS_STATES.AT_PICKUP_POINT
  //     ? {
  //         name: savedValues.address.name,
  //         detailedAddress: {
  //           address: 'AVENIDA DE LA CONSTITUCIÓN 20',
  //           address_2: 'TORREJÓN DE ARDOZ',
  //           country_code: 'ES',
  //           gps_latitude: 40.4547355,
  //           gps_longitude: -3.4898714,
  //           postal_code: '28850',
  //         },
  //         address: savedValues.address.address,
  //         phoneNumber: savedValues.address.phone,
  //         schedule: savedValues.address.schedule,
  //       }
  //     : undefined,
  // )

  const enhancedFormik = useEnhancedFormik(shippingSend2ToPropTypes, formik, {
    fieldErrors: serverFieldErrors,
  })

  const { handleDiscardConfirmation, hasServerError, hasAnyError } =
    useShippingSendStep({
      formik,
      enhancedFormik,
      onSetDirty,
      hasConfirmation,
      onAcceptConfirmation,
      serverFieldErrors,
      propTypes: shippingSend2ToPropTypes,
    })

  const [addressBook, setAddressBook] = useState(
    savedValues !== null && savedValues.addressBook
      ? savedValues.addressBook
      : null,
  )

  const selectDeliveryPointAddress = address => {
    formik.setValues(values => ({
      ...values,
      addressState: ADDRESS_STATES.AT_PICKUP_POINT,
      addressBook: null,
      selectedDeliveryPoint: address,
      address: {
        ...values.address,
        country: {
          code: address?.detailedAddress?.country_code ?? 'ES',
          label: new Intl.DisplayNames(['es'], { type: 'region' }).of(
            address?.detailedAddress?.country_code ?? 'ES',
          ),
        },
        name: '',
        contactName: address?.name ?? '',
        address: address?.detailedAddress?.address ?? '',
        phone: '',
        phonePrefix: DEFAULT_PHONE_PREFIX,
        email: '',
        stair: '',
        floor: '',
        letter: '',
        postalCode: address?.detailedAddress?.postal_code ?? '',
        province: '',
        city: address?.detailedAddress?.address_2 ?? '',
        hasSeveralLocalities: false,
        localitiesOptions: null,
        schedule: address?.schedule ?? null,
        organicPointCode: address?.organicPointCode ?? '',
      },
      comments: '',
    }))
  }

  const handleChangeBookAddress = async addressData => {
    const completeAddress = getCompleteAddressFromData(
      { ...addressData, addressState: ADDRESS_STATES.EXISTING },
      intl,
      t,
    )

    // REVIEW no he podido investigar por qué se llama esta función nada más
    // montar el componente. En ese primer ciclo, la propiedad
    // `completeAddress.id` no existe, por lo que en ese momento todavía no
    // tenemos que guardar la dirección de la agenda en el estado.
    // TODO investigar el origen de esa llamada al montar el componente para
    // que únicamente se llame cuando el usuario selecciona una dirección de
    // la agenda.
    if (completeAddress.id !== undefined) {
      setAddressBook({ ...completeAddress, isAddressBook: true })
    }

    await setFormikStateAndValidate(formik, state => ({
      values: {
        ...state.values,
        addressState: ADDRESS_STATES.EXISTING,
        address: { ...completeAddress },
        addressBook: { ...completeAddress, isAddressBook: true },
      },

      touched: {
        ...state.touched,
        addressState: true,
        name: true,
        address: true,
        autocompleteAddress: true,
        route: true,
        streetNumber: true,
        country: true,
        city: true,
        postalCode: true,
        phone: true,
        email: true,
      },
    }))
  }

  const [defaultCentre, ...restCentres] = centres
  const addressList = (
    centres.length === 1
      ? []
      : [
          formatCentreAddress(defaultCentre, true),
          ...restCentres.map(centre => formatCentreAddress(centre, false)),
        ]
  )
    .concat(addressBook)
    .filter(address => address !== null)
    .filter(address => address.id !== originCentreId && address.id !== '')

  const selectAddress = address => {
    formik.setValues(values => ({
      ...values,
      address: { ...address },
      addressBook: address.isAddressBook ? address : null,
    }))
  }

  const debouncedPostalCode = useDebounce(
    formik.values.address.postalCode,
    DEBOUNCED_POSTAL_CODE_DELAY,
  )

  useEffect(() => {
    if (formik.values.addressState === ADDRESS_STATES.EXISTING) {
      if (centres.length === 1) {
        // Este sería el caso en que el usuario está cambiando al tab de "En
        // una de mis direcciones" y solamente tiene un centro asociado.
        // No es necesario hacer nada, lo dejo comentado por si en un futuro
        // se quiere incluir algún comportamiento en concreto con
        // esta casuística.
      } else if (
        savedValues === null ||
        savedValues.addressState !== ADDRESS_STATES.EXISTING
      ) {
        formik.setValues(values => ({
          ...values,
          address: {
            ...addressList[0],
          },
        }))
      } else {
        formik.setValues(savedValues)
      }
    }
    // TODO: revisar las dependencias de este hook. He probado a
    // memorizar algunas pero produce un renderizado en bucle.
    // Puede que sea porque formik no devuelve una referencia estable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.addressState])

  useEffect(() => {
    if (
      !initialLoad &&
      debouncedPostalCode !== undefined &&
      debouncedPostalCode.trim() !== '' &&
      formik.values.address.postalCode !== '' &&
      formik.values.address.country.code !== '' &&
      formik.values.address.country?.code !== undefined
    ) {
      calculateProvinceAndLocalities(
        debouncedPostalCode,
        formik.values.address.country.code,
        fromSearch,
      )
        .then(result => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              ...result,
            },
          }))

          setZeroResultsGeocode(false)

          const errors = { ...formik.errors }

          if (formik.errors.address?.postalCode)
            delete errors.address.postalCode

          if (formik.errors.address?.city) delete errors.address.city

          if (formik.errors.address?.province) delete errors.address.province

          formik.setErrors(errors)
        })
        .catch(error => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              city: '',
              province: '',
              hasSeveralLocalities: false,
              localitiesOptions: null,
            },
          }))

          if (error.code === GEOCODING_ERRORS.zeroResults) {
            setZeroResultsGeocode(true)
          }
        })
    } else {
      setInitialLoad(false)
    }
  }, [debouncedPostalCode, formik.values.address.country?.code, fromSearch])

  useEffect(() => {
    if (hasConfirmation && !hasAnyError)
      onSubmitSuccess({ ...formik.values, addressBook })
  }, [enhancedFormik, formik, hasAnyError, hasConfirmation])

  return (
    <ShippingSendForm onSubmit={enhancedFormik.handleSubmit}>
      <Step2DestinationUI
        countries={countries}
        originCentreId={originCentreId}
        centreCode={centreCode}
        values={formik.values}
        touched={formik.touched}
        zeroResultsGeocode={zeroResultsGeocode}
        fieldErrors={enhancedFormik.fieldErrors}
        formErrors={enhancedFormik.formErrors}
        onChange={e => {
          setFromSearch(false)
          if (
            e.target.name === 'addressState' &&
            e.target.value === ADDRESS_STATES.NEW
          ) {
            formik.setValues(INITIAL_VALUES)
          } else if (
            e.target.name === 'addressState' &&
            e.target.value === ADDRESS_STATES.AT_PICKUP_POINT
          ) {
            formik.setValues({
              ...INITIAL_VALUES,
              addressState: ADDRESS_STATES.AT_PICKUP_POINT,
            })
          } else {
            enhancedFormik.handleChange(e)
          }
        }}
        setErrors={formik.setErrors}
        autofillAddress={addressInfo => {
          setFromSearch(addressInfo?.place)
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              ...addressInfo,
            },
          }))
        }}
        onBlur={e => {
          formik.validateField(e.target.name)
        }}
        isCenterWithAddressesBook
        onClickOpenAgenda={() => {
          setIsAddressBookOpen(true)
        }}
        addressList={addressList}
        selectAddress={selectAddress}
        changeDeliveryPointaddress={changeDeliveryPointAddress}
        deliveryPoints={deliveryPoints.data ?? []}
        selectDeliveryPointAddress={selectDeliveryPointAddress}
        selectedDeliveryPoint={formik.values.selectedDeliveryPoint}
        searchedAddress={formik.values.deliveryPointAddress}
        confirmAddress={confirmAddress}
        confirmedAddress={formik.values.confirmedAddress}
        handlePickUpErrors={handlePickUpErrors}
      />

      <ActionButtons
        isPrimaryDisabled={
          (formik.values.addressState === ADDRESS_STATES.EXISTING &&
            centres.length === 1 &&
            formik.values.addressBook === null) ||
          (formik.values.addressState === ADDRESS_STATES.AT_PICKUP_POINT &&
            formik.values.confirmedAddress === undefined) ||
          (formik.values.addressState === ADDRESS_STATES.AT_PICKUP_POINT &&
            formik.values.address.email === '') ||
          (centres.find(({ id }) => formik.values.address.id === id) &&
            formik.values.address.phone === '') ||
          error ||
          Object.keys(enhancedFormik?.fieldErrors?.address ?? {})?.length > 0
        }
        isDirty={formik.dirty}
        isSaved={savedValues !== null}
        onCancel={onCancel}
        hasOtherError={hasOtherError}
        hasServerError={hasServerError}
        currentStep={STEPS.TO}
        activeStep={activeStep}
      />
      <AddressBook
        currentStep={STEPS.TO}
        centerCode={centreCode}
        isOpen={isAddressBookOpen}
        close={closeAddressBook}
        selectDirection={handleChangeBookAddress}
      />

      {hasConfirmation && hasAnyError ? (
        <ConfirmationWithErrorModal
          onDiscard={handleDiscardConfirmation}
          onAmend={onCancelConfirmation}
        />
      ) : null}
    </ShippingSendForm>
  )
}

Step2Destination.propTypes = {
  centreCode: PropTypes.string.isRequired,
  savedValues: nullablePropType(
    makeValuesPropType(shippingSend2ToPropTypes).isRequired,
  ),
  activeStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  serverFieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSend2ToPropTypes).isRequired,
  ),
  hasOtherError: PropTypes.bool.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSetDirty: PropTypes.func.isRequired,
  hasConfirmation: PropTypes.bool.isRequired,
  onCancelConfirmation: PropTypes.func.isRequired,
  onAcceptConfirmation: PropTypes.func.isRequired,
}

export default Step2Destination
